"use client";
import "@growth/page.products/style.css";
import "@shared/ui.primitives/style.css";
import {
  MobileProductAttributesContainer,
  MobileTopBlockContainer,
  PearlPage,
  ScrollContainer,
  TopBlock,
  ProductAttributes,
  PricingActionsMobileWrapper,
  MobileDescriptionContainer,
  Description,
  ProductContainer,
  Highlights,
  HighlightsContainer
} from "@growth/page.products";
import type { ItemType, PDP } from "@shared/graphql.types";
import { InfoBlock } from "@shared/ui.components";
import { Typography } from "@shared/ui.primitives";

import { Gallery } from "packages/acquisition/Gallery/Gallery";
import debounce from "lodash.debounce";
import { addWishlistItemBySKU, removeWishlistItemBySKU } from "lib/api";
import { useUserV2 } from "hooks/useUserV2";
import { navigate } from "utils/routes";
import { useVariantIds } from "hooks/useVariantIds";
import { useLocale } from "hooks";
import { LoadingMask } from "components/LoadingMask";
import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation";
import { useMemo, useState } from "react";
import {
  onAddToBag,
  PricingOptions,
  PerfectFor,
  TraditionalRetail,
  ExtraProductInfo,
  HowToUse,
  ClinicalTrials,
  ReviewsLayout,
  Recommendations
} from "packages/acquisition/components";
import { getTrackingEventId } from "lib/api/utils/trackingEventId";
import { useTrackProductViewed } from "./hooks/useTrackProductViewed";
import { CoreProductInfoContainer } from "packages/acquisition/components/CoreProductInfoContainer/CoreProductInfoContainer";
import { useOOSEmailReminder } from "./hooks/useOOSEmailReminder";
import { dispatch } from "@shared/utils.events";

type PageProps = {
  productDetails: PDP;
  useCloudinary: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant?: boolean;
};

export const Page: React.FC<PageProps> = ({
  productDetails,
  useCloudinary,
  membershipInBag,
  isMember,
  isFreeTrialVariant = false
}) => {
  const eventId = useMemo(() => getTrackingEventId(), []);

  const { gallery, coreProductInfo, productDeepDive } = productDetails;
  const [confirmation, setConfirmation] = useState(false);
  const { wishlist, isLoggedIn, hasMembership } = useUserV2();
  const locale = useLocale();

  useTrackProductViewed(productDetails, eventId);

  const {
    size,
    madeIn,
    standards,
    prices,
    sku,
    title,
    brandName,
    subtitle,
    review,
    infoBanner,
    ribbons,
    relatedVariants,
    slug,
    membershipOptions,
    pricingOptions,
    id,
    isOutOfStock,
    description,
    badge
  } = coreProductInfo;
  const {
    perfectFor,
    productBenefits,
    listOfIngredients,
    candleSafety,
    highlights,
    howToUse,
    trials,
    traditionalRetail
  } = productDeepDive;

  const attributes = [size, madeIn, ...standards];
  const filteredAttributes = attributes.filter(
    (attribute) => typeof attribute === "string"
  );

  const { giftMembershipProductId } = useVariantIds();
  const isGiftMembership = id === giftMembershipProductId;

  const handleAddRemoveToWishlist = debounce((isInWishlist: boolean) => {
    if (isInWishlist) removeWishlistItemBySKU(sku);
    else addWishlistItemBySKU(sku);
  }, 500);
  const oosEmailReminder = useOOSEmailReminder(sku);

  const isInWishlist = wishlist?.some(
    (item: ItemType) => item.variant?.sku === sku
  );

  const handleWishlist = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    handleAddRemoveToWishlist(isInWishlist);
  };

  const handleOutOfStockSubmit = ({ email }: { email: string }) => {
    return oosEmailReminder.handleOutOfStockEmail(email, locale);
  };

  const onATB = onAddToBag({
    variant: { id, sku },
    isGiftMembership,
    locale,
    hasMembership,
    setConfirmation
  });

  const trackReviewsClick = () => {
    dispatch("page element clicked", {
      target: "PDP - Reviews Link",
      details: "Scroll To Reviews",
      location: "PDP - Core Product Info"
    });
  };
  return (
    <PearlPage>
      <script
        id="pdp-seo-tag"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(productDetails.seo)
        }}
      />

      <ATBConfirmation
        variant={{ sku }}
        confirmation={confirmation}
        setConfirmation={setConfirmation}
        source="PDP"
        isFreeTrialVariant={isFreeTrialVariant}
      />
      <ProductContainer>
        <ScrollContainer>
          <MobileTopBlockContainer>
            <TopBlock
              brandName={brandName ?? undefined}
              title={title}
              subtitle={subtitle}
              rating={review.averageRating}
              reviewCount={review.count}
              ribbons={ribbons ?? undefined}
              trackReviewsClick={trackReviewsClick}
            />
            {infoBanner && (
              <InfoBlock>
                <Typography color="primary-dark" variant="body-md">
                  {infoBanner}
                </Typography>
              </InfoBlock>
            )}
          </MobileTopBlockContainer>
          <Gallery
            badge={badge ?? undefined}
            gallery={gallery}
            useCloudinary={useCloudinary}
          />
          <PricingActionsMobileWrapper>
            <PricingOptions
              membershipOptions={membershipOptions}
              pricingOptions={pricingOptions}
              variant={{ sku, prices }}
              onAddToBag={onATB}
              handleWishlist={handleWishlist}
              isInWishlist={isInWishlist}
              relatedVariants={relatedVariants}
              selectedVariantSlug={slug}
              context="mobile"
              handleSubmit={handleOutOfStockSubmit}
              isOutOfStock={isOutOfStock}
              submitted={oosEmailReminder.emailSent}
              membershipInBag={membershipInBag}
              isMember={isMember}
              isFreeTrialVariant={isFreeTrialVariant}
            />
          </PricingActionsMobileWrapper>
          <MobileDescriptionContainer>
            <Description text={description ?? ""} />
          </MobileDescriptionContainer>
          <PerfectFor perfectFor={perfectFor} />
          <MobileProductAttributesContainer>
            <ProductAttributes attributes={filteredAttributes} />
          </MobileProductAttributesContainer>
          {(!perfectFor.items || !perfectFor.image) && !productBenefits && (
            <HighlightsContainer>
              <Highlights text={highlights ?? ""} />
            </HighlightsContainer>
          )}
          <TraditionalRetail traditionalRetail={traditionalRetail} />
          <ExtraProductInfo
            productBenefits={productBenefits ?? undefined}
            ingredientList={listOfIngredients ?? undefined}
            candleSafety={candleSafety ?? undefined}
          />
          {howToUse && <HowToUse {...howToUse} />}
          {trials && <ClinicalTrials trials={trials} />}
        </ScrollContainer>
        <CoreProductInfoContainer
          coreProductInfo={coreProductInfo}
          filteredAttributes={filteredAttributes}
          trackReviewsClick={trackReviewsClick}
          onATB={onATB}
          handleWishlist={handleWishlist}
          isInWishlist={isInWishlist}
          relatedVariants={relatedVariants}
          selectedVariantSlug={slug}
          handleSubmit={handleOutOfStockSubmit}
          submitted={oosEmailReminder.emailSent}
          membershipInBag={membershipInBag}
          isMember={isMember}
          isFreeTrialVariant={isFreeTrialVariant}
        />
      </ProductContainer>
      <Recommendations isOutOfStock={isOutOfStock} sku={sku} />
      <ReviewsLayout sku={sku} />
      <LoadingMask />
    </PearlPage>
  );
};
